import React from 'react';
import { Link } from 'react-router-dom';
import ProposalStatusBadge from './ProposalStatusBadge';
import ProposalRatingBadge from './ProposalRatingBadge';

const formatCost = (usd) => {
  if (usd == null) return '—';
  return `$${Number(usd).toFixed(2)}`;
};

const timeDuration = (dateStr) => {
  if (!dateStr) return null;
  const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  const days = Math.floor(diff / 86400);
  if (days === 1) return '1 day';
  if (days < 30) return `${days} days`;
  return `${Math.floor(days / 30)}mo`;
};

const STATUS_TIME_CONFIG = {
  pending_qa: { label: 'Under review for', duration: true, tooltip: 'Time since submitted for review' },
  draft: { label: 'In draft for', duration: true, tooltip: 'Time since proposal was created' },
  approved: { label: 'Approved', duration: false, tooltip: 'Time since proposal was approved' },
  rejected: { label: 'Rejected', duration: false, tooltip: 'Time since proposal was rejected' },
  sent_back: { label: 'Sent back', duration: false, tooltip: 'Time since sent back for update' },
  sent_to_client: { label: 'Sent', duration: false, tooltip: 'Time since sent to client' },
  client_rejected: { label: 'Client feedback', duration: false, tooltip: 'Time since client sent back for changes' },
  accepted: { label: 'Accepted', duration: false, tooltip: 'Time since client accepted' },
};

const getContextualTime = (proposal, activeTab) => {
  const config = STATUS_TIME_CONFIG[activeTab];
  if (!config) return null;

  // For draft, time is relative to created_at; for all others, updated_at (status change)
  const refDate = activeTab === 'draft' ? proposal.created_at : proposal.updated_at;
  const elapsed = timeDuration(refDate);
  if (!elapsed) return null;

  // "Under review for 21h" vs "Approved 21h ago"
  const text = config.duration ? `${config.label} ${elapsed}` : `${config.label} ${elapsed} ago`;

  return {
    text,
    tooltip: config.tooltip,
  };
};

const ProposalCard = ({ proposal, activeTab }) => {
  if (!proposal) return null;

  const isActive = proposal.is_active;
  const detailUrl = `/leads/${proposal.lead_id}/proposals/${proposal.id}`;
  const contextTime = getContextualTime(proposal, activeTab);

  return (
    <div
      className="flex items-start sm:items-center gap-3 sm:gap-5 px-4 sm:px-5 py-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
    >
      {/* Active indicator */}
      <div className="shrink-0 mt-1.5 sm:mt-0">
        {isActive ? (
          <span className="relative flex h-2.5 w-2.5 rounded-full bg-primary-500" title="Active version">
            <span className="animate-ping absolute inline-flex h-2.5 w-2.5 rounded-full bg-primary-400 opacity-75"></span>
          </span>
        ) : (
          <span className="flex h-2.5 w-2.5 rounded-full bg-gray-300 dark:bg-gray-600" title="Inactive"></span>
        )}
      </div>

      {/* Lead & Proposal Info */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 mb-0.5">
          <h3 className="text-sm font-semibold truncate">
            <Link
              to={detailUrl}
              onClick={(e) => e.stopPropagation()}
              className="text-gray-900 dark:text-gray-100 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
            >
              {proposal.lead_company || proposal.lead_name || 'Unknown Lead'}
            </Link>
          </h3>
          {proposal.version > 1 && (
            <span className="shrink-0 text-[10px] font-bold text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">
              v{proposal.version}
            </span>
          )}
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
          {proposal.title || 'Untitled Proposal'}
          {proposal.lead_name && proposal.lead_company && (
            <span className="text-gray-400 dark:text-gray-500"> &middot; {proposal.lead_name}</span>
          )}
        </p>
        {/* Stats row */}
        <div className="flex items-center gap-3 mt-1.5 text-[11px] text-gray-400 dark:text-gray-500">
          {contextTime && (
            <span className="inline-flex items-center gap-1" title={contextTime.tooltip}>
              <svg className="w-3 h-3 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              {contextTime.text}
            </span>
          )}
          {proposal.total_cost_usd != null && (
            <>
              {contextTime && <span className="text-gray-300 dark:text-gray-600">&middot;</span>}
              <span className="inline-flex items-center gap-1" title="Total Cost">
                <svg className="w-3 h-3 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                {formatCost(proposal.total_cost_usd)}
              </span>
            </>
          )}
          {proposal.client_review_url && (
            <>
              {(contextTime || proposal.total_cost_usd != null) && <span className="text-gray-300 dark:text-gray-600">&middot;</span>}
              {proposal.client_email && (
                <span className="inline-flex items-center gap-1 text-gray-500 dark:text-gray-400" title="Client email with access">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                  {proposal.client_email}
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(proposal.client_email);
                      } catch {
                        const el = document.createElement('textarea');
                        el.value = proposal.client_email;
                        el.style.position = 'fixed';
                        el.style.opacity = '0';
                        document.body.appendChild(el);
                        el.focus();
                        el.select();
                        document.execCommand('copy');
                        document.body.removeChild(el);
                      }
                    }}
                    className="p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    title="Copy email"
                  >
                    <svg className="w-3.5 h-3.5 text-gray-700 dark:text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                  </button>
                </span>
              )}
              {proposal.client_email && <span className="text-gray-300 dark:text-gray-600">&middot;</span>}
              <a
                href={proposal.client_review_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 hover:underline transition-colors"
                title="Open client review page"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
                Client View
              </a>
            </>
          )}
        </div>
        {/* Mobile: status + rating below the title */}
        <div className="flex items-center flex-wrap gap-2 mt-2 sm:hidden">
          <ProposalStatusBadge status={proposal.status} />
          {proposal.rating != null && (
            <ProposalRatingBadge rating={proposal.rating} size="sm" />
          )}
        </div>
      </div>

      {/* Status Badge (desktop) */}
      <div className="hidden sm:block shrink-0">
        <ProposalStatusBadge status={proposal.status} />
      </div>

      {/* Rating (desktop) */}
      <div className="hidden sm:block shrink-0">
        {proposal.rating != null ? (
          <ProposalRatingBadge rating={proposal.rating} size="sm" />
        ) : null}
      </div>

      {/* Open in new tab */}
      <a
        href={detailUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="shrink-0 p-1.5 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        title="Open in new tab"
        aria-label="Open in new tab"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
        </svg>
      </a>
    </div>
  );
};

export default ProposalCard;
